// Hardcoded sample data for the kitting POC. Replace with real session-driven data
// once the concept is wired up.

import type { Kit } from "~restock/orders/kit-builder/helpers/computeKits";

export interface Carrier {
	// e.g. "DUTCH CART", "CANNON CART"
	code: string;
	// Used to derive per-layer height when mixing items: heightPerLayer_i = totalHeightInches / maxLayersPerCarrier_i.
	// We don't actually need the absolute value to compute fit (the constraint normalizes), but it's useful for display.
	totalHeightInches: number;
	name: string;
}

export interface Site {
	code: string;
	defaultCarrierCode: string;
	name: string;
}

/**
 * Per-(item-or-category, carrier) packing config.
 * Item-level config takes precedence; otherwise we fall back to category-level.
 */
export interface CarrierConfig {
	carrierCode: string;
	categoryCode: string | null;
	maxLayersPerCarrier: number;
	shipUnitsPerLayer: number;
	skuId: string | null;
}

export type DefaultCarrierMode = "item" | "site";

export interface Sku {
	categoryCode: string;
	defaultCarrierCode: string;
	description: string;
	// Conversion to lowest UOM. If the item ships as eaches (no tray), this is 1.
	eachesPerShipUnit: number;
	id: string;
	itemNumber: string;
	// Primary site for the SKU — used as a tiebreaker when picking the kit's site
	// for carrier resolution. Per-site availability lives in `fakeSkuSiteAvails`.
	locationCode: string;
	name: string;
	// Sum of per-site availabilities. Derived from `fakeSkuSiteAvails`.
	totalAvailable: number;
	// Unit retail price; drives the Sales $ column when scoped to a date range.
	unitPrice: number;
}

// Date-range options for the Order Demand / Sales $ columns. Each one resolves
// to a number of weeks of weekly-rate-of-sale used to scale the displayed values.
export type DateRange = "WTD" | "MTD" | "STD" | "L6W";

export const dateRangeWeeks: Record<DateRange, number> = {
	L6W: 6,
	MTD: 4,
	STD: 13,
	WTD: 1
};

export interface Store {
	division: string;
	id: string;
	market: string;
	name: string;
	region: string;
}

export interface SkuStoreCell {
	demand: number;
	onHand: number;
	skuId: string;
	storeId: string;
	// Last-6-weeks weekly demand history, oldest → newest. Drives the SKU velocity sparkline.
	weeklyDemandHistory: number[];
	weeklyRateOfSale: number;
}

// Per-(sku, site) on-hand availability. A SKU can live at one or more sites.
export interface SkuSiteAvail {
	available: number;
	siteCode: string;
	skuId: string;
}

export const fakeCarriers: Carrier[] = [
	{ code: "DUTCH CART", name: "Dutch Cart", totalHeightInches: 100 },
	{ code: "CANNON CART", name: "Cannon Cart", totalHeightInches: 110 },
	{ code: "PALLET", name: "Pallet", totalHeightInches: 80 }
];

export const fakeSites: Site[] = [
	{ code: "TPC", defaultCarrierCode: "DUTCH CART", name: "The Plant Company" },
	{ code: "GL", defaultCarrierCode: "DUTCH CART", name: "Green Legacy" },
	{ code: "CMG", defaultCarrierCode: "CANNON CART", name: "Catoctin Mountain Growers" }
];

export const fakeDefaultCarrierMode: DefaultCarrierMode = "item";

// Per-(sku, site) availability. Each SKU may live at one or more sites; total
// availability on `Sku.totalAvailable` is the sum across sites.
export const fakeSkuSiteAvails: SkuSiteAvail[] = [
	{ available: 420, siteCode: "TPC", skuId: "sku-1" },
	{ available: 180, siteCode: "GL", skuId: "sku-1" },
	{ available: 200, siteCode: "TPC", skuId: "sku-2" },
	{ available: 120, siteCode: "CMG", skuId: "sku-2" },
	{ available: 500, siteCode: "TPC", skuId: "sku-3" },
	{ available: 200, siteCode: "GL", skuId: "sku-3" },
	{ available: 100, siteCode: "CMG", skuId: "sku-3" },
	{ available: 240, siteCode: "TPC", skuId: "sku-4" },
	{ available: 800, siteCode: "TPC", skuId: "sku-5" },
	{ available: 700, siteCode: "CMG", skuId: "sku-5" },
	{ available: 600, siteCode: "TPC", skuId: "sku-6" },
	{ available: 600, siteCode: "GL", skuId: "sku-6" },
	{ available: 500, siteCode: "TPC", skuId: "sku-7" },
	{ available: 400, siteCode: "CMG", skuId: "sku-7" },
	{ available: 300, siteCode: "TPC", skuId: "sku-8" },
	{ available: 150, siteCode: "GL", skuId: "sku-8" },
	{ available: 380, siteCode: "GL", skuId: "sku-9" },
	{ available: 180, siteCode: "TPC", skuId: "sku-10" },
	{ available: 100, siteCode: "CMG", skuId: "sku-10" }
];

const sumSiteAvail = (skuId: string): number =>
	fakeSkuSiteAvails.filter((a) => a.skuId === skuId).reduce((sum, a) => sum + a.available, 0);

export const fakeSkus: Sku[] = [
	{
		categoryCode: "1G-SHRUB",
		defaultCarrierCode: "DUTCH CART",
		description: "1 gal flowering shrub",
		eachesPerShipUnit: 1,
		id: "sku-1",
		itemNumber: "HYD-1G",
		locationCode: "TPC",
		name: "Hydrangea Endless Summer 1G",
		totalAvailable: sumSiteAvail("sku-1"),
		unitPrice: 14.99
	},
	{
		categoryCode: "2G-SHRUB",
		defaultCarrierCode: "DUTCH CART",
		description: "2 gal flowering shrub",
		eachesPerShipUnit: 1,
		id: "sku-2",
		itemNumber: "HYD-2G",
		locationCode: "TPC",
		name: "Hydrangea Endless Summer 2G",
		totalAvailable: sumSiteAvail("sku-2"),
		unitPrice: 24.99
	},
	{
		categoryCode: "1G-EVERGREEN",
		defaultCarrierCode: "DUTCH CART",
		description: "1 gal evergreen",
		eachesPerShipUnit: 1,
		id: "sku-3",
		itemNumber: "BOX-1G",
		locationCode: "TPC",
		name: "Boxwood Green Velvet 1G",
		totalAvailable: sumSiteAvail("sku-3"),
		unitPrice: 12.99
	},
	{
		categoryCode: "3G-EVERGREEN",
		defaultCarrierCode: "DUTCH CART",
		description: "3 gal evergreen",
		eachesPerShipUnit: 1,
		id: "sku-4",
		itemNumber: "BOX-3G",
		locationCode: "TPC",
		name: "Boxwood Green Velvet 3G",
		totalAvailable: sumSiteAvail("sku-4"),
		unitPrice: 34.99
	},
	{
		categoryCode: "POT-12",
		defaultCarrierCode: "PALLET",
		description: "12in decorative pot",
		// Pots ship in trays of 4.
		eachesPerShipUnit: 4,
		id: "sku-5",
		itemNumber: "POT-12",
		locationCode: "TPC",
		name: "Terracotta Pot 12in",
		totalAvailable: sumSiteAvail("sku-5"),
		unitPrice: 19.99
	},
	{
		categoryCode: "SOIL",
		defaultCarrierCode: "PALLET",
		description: "0.5 cu ft soil mix",
		eachesPerShipUnit: 1,
		id: "sku-6",
		itemNumber: "SOIL-05",
		locationCode: "TPC",
		name: "Premium Potting Mix 0.5cf",
		totalAvailable: sumSiteAvail("sku-6"),
		unitPrice: 7.99
	},
	{
		categoryCode: "FERT",
		defaultCarrierCode: "PALLET",
		// Bagged fert ships in trays of 6.
		description: "1 lb slow release",
		eachesPerShipUnit: 6,
		id: "sku-7",
		itemNumber: "FERT-1",
		locationCode: "TPC",
		name: "Osmocote Fertilizer 1lb",
		totalAvailable: sumSiteAvail("sku-7"),
		unitPrice: 9.99
	},
	{
		categoryCode: "1G-PERENNIAL",
		defaultCarrierCode: "DUTCH CART",
		description: "1 gal perennial",
		eachesPerShipUnit: 1,
		id: "sku-8",
		itemNumber: "LAV-1G",
		locationCode: "TPC",
		name: "Lavender Hidcote 1G",
		totalAvailable: sumSiteAvail("sku-8"),
		unitPrice: 9.99
	},
	{
		categoryCode: "1G-GRASS",
		defaultCarrierCode: "DUTCH CART",
		description: "1 gal ornamental grass",
		eachesPerShipUnit: 1,
		id: "sku-9",
		itemNumber: "PEN-1G",
		locationCode: "GL",
		name: "Pennisetum Hameln 1G",
		totalAvailable: sumSiteAvail("sku-9"),
		unitPrice: 11.99
	},
	{
		categoryCode: "2G-SHRUB",
		defaultCarrierCode: "DUTCH CART",
		description: "2 gal climbing rose",
		eachesPerShipUnit: 1,
		id: "sku-10",
		itemNumber: "ROS-2G",
		locationCode: "TPC",
		name: "Rose Knockout 2G",
		totalAvailable: sumSiteAvail("sku-10"),
		unitPrice: 22.99
	}
];

/**
 * Carrier configs. Item-level (skuId set) takes precedence; otherwise the category-level
 * row matches every SKU in that category. Order doesn't matter — `resolveItemConfig`
 * does the lookup.
 */
export const fakeCarrierConfigs: CarrierConfig[] = [
	// Category-level fallbacks for Dutch Cart
	{ carrierCode: "DUTCH CART", categoryCode: "1G-SHRUB", maxLayersPerCarrier: 5, shipUnitsPerLayer: 8, skuId: null },
	{
		carrierCode: "DUTCH CART",
		categoryCode: "2G-SHRUB",
		maxLayersPerCarrier: 4,
		shipUnitsPerLayer: 6,
		skuId: null
	},
	{
		carrierCode: "DUTCH CART",
		categoryCode: "1G-EVERGREEN",
		maxLayersPerCarrier: 5,
		shipUnitsPerLayer: 8,
		skuId: null
	},
	{
		carrierCode: "DUTCH CART",
		categoryCode: "3G-EVERGREEN",
		maxLayersPerCarrier: 3,
		shipUnitsPerLayer: 4,
		skuId: null
	},
	{
		carrierCode: "DUTCH CART",
		categoryCode: "1G-PERENNIAL",
		maxLayersPerCarrier: 5,
		shipUnitsPerLayer: 8,
		skuId: null
	},
	{ carrierCode: "DUTCH CART", categoryCode: "1G-GRASS", maxLayersPerCarrier: 5, shipUnitsPerLayer: 8, skuId: null },

	// Cannon Cart fallbacks (different config per category)
	{ carrierCode: "CANNON CART", categoryCode: "1G-SHRUB", maxLayersPerCarrier: 6, shipUnitsPerLayer: 9, skuId: null },
	{ carrierCode: "CANNON CART", categoryCode: "2G-SHRUB", maxLayersPerCarrier: 5, shipUnitsPerLayer: 6, skuId: null },
	{
		carrierCode: "CANNON CART",
		categoryCode: "1G-EVERGREEN",
		maxLayersPerCarrier: 6,
		shipUnitsPerLayer: 9,
		skuId: null
	},

	// Pallet configs
	{ carrierCode: "PALLET", categoryCode: "POT-12", maxLayersPerCarrier: 4, shipUnitsPerLayer: 16, skuId: null },
	{ carrierCode: "PALLET", categoryCode: "SOIL", maxLayersPerCarrier: 6, shipUnitsPerLayer: 12, skuId: null },
	{ carrierCode: "PALLET", categoryCode: "FERT", maxLayersPerCarrier: 5, shipUnitsPerLayer: 8, skuId: null },

	// Item-level override example: Hydrangea 1G fits more on Dutch Cart than the category default.
	{ carrierCode: "DUTCH CART", categoryCode: null, maxLayersPerCarrier: 6, shipUnitsPerLayer: 9, skuId: "sku-1" }
];

export const fakeStores: Store[] = [
	// North → Mid-Atlantic → 252
	{ division: "North", id: "store-1", market: "252", name: "HOME DEPOT ANNAPOLIS #1234", region: "Mid-Atlantic" },
	{ division: "North", id: "store-2", market: "252", name: "HOME DEPOT BALTIMORE #1235", region: "Mid-Atlantic" },
	{ division: "North", id: "store-3", market: "252", name: "HOME DEPOT FREDERICK #1236", region: "Mid-Atlantic" },
	// North → Mid-Atlantic → 263
	{ division: "North", id: "store-4", market: "263", name: "HOME DEPOT PHILADELPHIA #1402", region: "Mid-Atlantic" },
	{ division: "North", id: "store-5", market: "263", name: "HOME DEPOT KING OF PRUSSIA #1403", region: "Mid-Atlantic" },
	// North → New England → 271
	{ division: "North", id: "store-6", market: "271", name: "HOME DEPOT BOSTON #1601", region: "New England" },
	{ division: "North", id: "store-7", market: "271", name: "HOME DEPOT WORCESTER #1602", region: "New England" },
	// South → Carolinas → 318
	{ division: "South", id: "store-8", market: "318", name: "HOME DEPOT CHARLOTTE #2105", region: "Carolinas" },
	{ division: "South", id: "store-9", market: "318", name: "HOME DEPOT RALEIGH #2106", region: "Carolinas" },
	// South → Florida → 322
	{ division: "South", id: "store-10", market: "322", name: "HOME DEPOT ORLANDO #2310", region: "Florida" },
	{ division: "South", id: "store-11", market: "322", name: "HOME DEPOT TAMPA #2311", region: "Florida" },
	{ division: "South", id: "store-12", market: "322", name: "HOME DEPOT JACKSONVILLE #2312", region: "Florida" }
];

// Deterministic pseudo-random per (sku, store) so the POC renders consistently.
const hash = (s: string): number => {
	let h = 2166136261;
	for (let i = 0; i < s.length; i++) {
		h ^= s.charCodeAt(i);
		h = Math.imul(h, 16777619);
	}
	return Math.abs(h);
};

export const fakeKits: Kit[] = [
	{
		components: [
			{ multiple: 2, skuId: "sku-1" },
			{ multiple: 2, skuId: "sku-3" },
			{ multiple: 1, skuId: "sku-8" }
		],
		id: "kit-1",
		name: "Kit 1",
		unit: "layer"
	},
	{
		components: [
			{ multiple: 4, skuId: "sku-5" },
			{ multiple: 4, skuId: "sku-6" },
			{ multiple: 2, skuId: "sku-7" }
		],
		id: "kit-2",
		name: "Kit 2",
		unit: "tray"
	},
	{
		components: [
			{ multiple: 1, skuId: "sku-2" },
			{ multiple: 1, skuId: "sku-10" }
		],
		id: "kit-3",
		name: "Kit 3",
		unit: "layer"
	}
];

export const fakeSkuStoreCells: SkuStoreCell[] = fakeSkus.flatMap((sku) =>
	fakeStores.map<SkuStoreCell>((store) => {
		const seed = hash(`${sku.id}|${store.id}`);
		const demand = 20 + (seed % 80);
		const onHand = (seed >> 3) % 30;
		const wros = 5 + ((seed >> 6) % 25);
		// Six weeks of demand history (oldest → newest), centered on wros with deterministic
		// per-week noise + a mild seasonality ramp so trends read visually in the sparkline.
		const weeklyDemandHistory = Array.from({ length: 6 }, (_, i) => {
			const weekSeed = hash(`${sku.id}|${store.id}|w${i}`);
			const noise = (weekSeed % 1000) / 1000; // 0..1
			const ramp = 0.85 + i * 0.05; // slight upward bias toward the most recent week
			return Math.max(1, Math.round(wros * ramp * (0.7 + noise * 0.6)));
		});
		return {
			demand,
			onHand,
			skuId: sku.id,
			storeId: store.id,
			weeklyDemandHistory,
			weeklyRateOfSale: wros
		};
	})
);
